import { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, ListMusic, Play, Pause, Trash2, Music, Sparkles, GripVertical, Edit2, Save, X, Download, Shuffle, ChevronUp, ChevronDown, TrendingUp, KeyRound, Activity } from 'lucide-react';
import { toast } from 'sonner';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { API, useAppContext } from '@/App';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { smartShuffle } from '@/utils/smartShuffle';
import { computeSetWarnings } from '@/utils/setWarnings';
import MixQualityCard from '@/components/MixQualityCard';
import useRestorePlayer from '@/utils/useRestorePlayer';
import { usePlayer } from '@/context/PlayerContext';
import AudioPlayer from '@/components/AudioPlayer';
import ExportModal from '@/components/ExportModal';
import GenreEditor from '@/components/GenreEditor';
import TrackCoverButton from '@/components/TrackCoverButton';
import WaveformBars from '@/components/WaveformBars';
import { AreaChart, Area, ResponsiveContainer } from 'recharts';

// Sortable Track Item Component — matches Crate grid layout — matches Crate grid layout
function SortableTrackItem({ track, index, totalTracks, nextTrack, onRemove, onPlay, isPlaying, isCurrentTrack, formatDuration, onTrackUpdate }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: track.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
    opacity: isDragging ? 0.8 : 1,
  };

  const COLS = '40px 56px 24px 1fr 52px 76px 52px 160px 48px 40px';

  return (
    <div ref={setNodeRef} style={style}>
      <div
        className={`grid px-4 py-2.5 items-center border transition-colors bg-white group ${
          isDragging
            ? 'border-[#2563FF] shadow-[0_8px_24px_rgba(37,99,255,0.18)]'
            : isCurrentTrack
              ? 'border-[#2563FF] bg-[#EFF6FF]'
              : 'border-[#E2E8F0] hover:border-[#2563FF]/50'
        }`}
        style={{ gridTemplateColumns: COLS, gap: '10px' }}
        data-testid={`playlist-track-${index}`}
      >
        {/* Drag handle */}
        <button
          className="cursor-grab active:cursor-grabbing p-1 text-[#94A3B8] hover:text-[#2563FF] touch-none flex items-center justify-center"
          {...attributes}
          {...listeners}
          data-testid={`drag-handle-${index}`}
        >
          <GripVertical className="w-4 h-4" />
        </button>

        {/* Cover */}
        <div>
          <TrackCoverButton
            track={track}
            isCurrentlyPlaying={isCurrentTrack}
            isPlaying={isPlaying}
            onPlayPause={onPlay}
            wrapperClassName="w-12 h-12"
            testIdPrefix={`play-track-${index}`}
          />
        </div>

        {/* Track number */}
        <span className="font-mono text-xs text-[#94A3B8] text-center">
          {index + 1}
        </span>

        {/* Title / Artist / Genre */}
        <div className="min-w-0">
          <Link to={`/track/${track.id}`} className="block min-w-0">
            <p className="font-medium text-[13px] text-[#3B82F6] truncate hover:text-[#2563FF] transition-colors">
              {track.title}
            </p>
            <p className="font-body text-[11px] text-[#64748B] truncate mt-0.5">{track.artist}</p>
          </Link>
          <div className="mt-1">
            <GenreEditor track={track} onChange={onTrackUpdate} />
          </div>
        </div>

        {/* BPM */}
        <div className="text-center">
          <span className="font-mono text-base text-[#0F172A]">
            {track.analysis?.bpm?.toFixed(0) || '--'}
          </span>
        </div>

        {/* Key / Camelot */}
        <div className="text-center">
          <span className="font-mono text-base text-[#2563FF] font-bold block">
            {track.analysis?.camelot || '--'}
          </span>
          <span className="font-mono text-[10px] text-[#64748B] block mt-0.5">
            {track.analysis?.key || '--'}
          </span>
        </div>

        {/* Energy */}
        <div className="text-center">
          <span className="font-mono text-base text-[#64748B]">
            {track.analysis?.energy?.toFixed(0) || '--'}
          </span>
        </div>

        {/* Waveform — matches Crate style */}
        <div className="h-[42px] rounded-lg bg-white/70 border border-[#E2E8F0] overflow-hidden relative">
          <WaveformBars
            structure={track.analysis?.structure}
            duration={track.duration || 1}
            progress={0}
            trackId={track.id}
          />
        </div>

        {/* Duration */}
        <span className="font-mono text-xs text-[#94A3B8] text-right">
          {formatDuration(track.duration)}
        </span>

        {/* Remove */}
        <button
          onClick={() => onRemove(track.id)}
          className="p-1.5 text-[#94A3B8] hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
          data-testid={`remove-track-${index}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Transition indicator */}
      {index < totalTracks - 1 && nextTrack && (
        <div className="px-4 py-1.5 bg-[#F8FAFC] border-x border-[#E2E8F0] flex items-center gap-2">
          <span className="font-mono text-xs text-[#94A3B8]">{track.analysis?.camelot}</span>
          <span className="text-[#2563FF] text-xs">→</span>
          <span className="font-mono text-xs text-[#94A3B8]">{nextTrack.analysis?.camelot}</span>
          {track.analysis?.camelot === nextTrack.analysis?.camelot ? (
            <span className="text-[10px] font-mono text-green-500 ml-1">Perfect match</span>
          ) : track.analysis?.camelot?.[0] === nextTrack.analysis?.camelot?.[0] ? (
            <span className="text-[10px] font-mono text-amber-500 ml-1">Relative key</span>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default function PlaylistDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { refreshCounts } = useAppContext();
  const [playlist, setPlaylist] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [setQuality, setSetQuality] = useState(null);
  // Audio player state — owned by global PlayerContext so the <audio>
  // element survives navigation and iOS lock-screen suspensions.
  const { playingTrack, isPlaying, playTrack, handleTrackChange, setIsPlaying } = usePlayer();

  // Auto-restore the player after iOS Safari kills the page on long locks.
  useRestorePlayer(tracks, playingTrack, () => {});

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Phase 2 DJ Coach — transition warnings computed from the resolved
  // track sequence. Works for both new and old AI sets without a DB migration.
  const setWarnings = useMemo(() => computeSetWarnings(tracks), [tracks]);
  const [warningsExpanded, setWarningsExpanded] = useState(false);

  // Calculate combined playlist energy data for the wave visualization
  const combinedEnergyData = useMemo(() => {
    if (tracks.length === 0) return [];
    
    const data = [];
    let totalDuration = tracks.reduce((sum, t) => sum + (t.duration || 180), 0);
    let currentPosition = 0;
    
    tracks.forEach((track, trackIndex) => {
      const trackDuration = track.duration || 180;
      
      if (track.analysis?.structure?.length > 0) {
        const structureTotalDuration = track.analysis.structure.reduce((sum, s) => sum + (s.duration || 1), 0);
        
        track.analysis.structure.forEach((seg) => {
          const segPosition = currentPosition + ((seg.duration || 1) / structureTotalDuration) * trackDuration;
          data.push({
            position: (currentPosition / totalDuration) * 100,
            energy: seg.energy || 50,
            trackIndex,
            trackTitle: track.title,
            type: seg.type,
          });
          currentPosition = segPosition;
        });
      } else {
        // No structure data - use overall energy
        data.push({
          position: (currentPosition / totalDuration) * 100,
          energy: track.analysis?.energy || 50,
          trackIndex,
          trackTitle: track.title,
          type: 'main',
        });
        currentPosition += trackDuration;
      }
    });
    
    // Add final point
    if (data.length > 0) {
      data.push({
        position: 100,
        energy: data[data.length - 1].energy,
        trackIndex: tracks.length - 1,
        trackTitle: tracks[tracks.length - 1]?.title,
        type: 'end',
      });
    }
    
    return data;
  }, [tracks]);

  // Track markers for the combined view
  const trackMarkers = useMemo(() => {
    if (tracks.length <= 1) return [];
    
    const markers = [];
    let totalDuration = tracks.reduce((sum, t) => sum + (t.duration || 180), 0);
    let position = 0;
    
    tracks.forEach((track, index) => {
      if (index > 0) {
        markers.push({
          position: (position / totalDuration) * 100,
          label: `${index + 1}`,
        });
      }
      position += track.duration || 180;
    });
    
    return markers;
  }, [tracks]);

  const fetchPlaylist = async () => {
    try {
      const response = await axios.get(`${API}/playlists/${id}`);
      setPlaylist(response.data.playlist);
      setTracks(response.data.tracks || []);
      setEditName(response.data.playlist?.name || '');
    } catch (e) {
      console.error('Failed to fetch playlist');
      toast.error('Playlist not found');
    } finally {
      setLoading(false);
    }
  };

  const fetchSetQuality = async () => {
    try {
      const { data } = await axios.get(`${API}/dj/set-quality/${id}`);
      setSetQuality(data);
    } catch (e) {
      // silent — non-critical
    }
  };

  useEffect(() => {
    fetchPlaylist();
    fetchSetQuality();
  }, [id]);

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const oldIndex = tracks.findIndex(t => t.id === active.id);
      const newIndex = tracks.findIndex(t => t.id === over.id);
      
      const newTracks = arrayMove(tracks, oldIndex, newIndex);
      setTracks(newTracks);
      
      // Save new order to backend
      try {
        const newTrackIds = newTracks.map(t => t.id);
        await axios.put(`${API}/playlists/${id}`, { track_ids: newTrackIds });
        toast.success('Order saved');
      } catch (e) {
        toast.error('Failed to save order');
        fetchPlaylist(); // Revert on error
      }
    }
  };

  const handleShuffle = async () => {
    if (tracks.length < 2) {
      toast.error('Need at least 2 tracks to shuffle');
      return;
    }
    const ids = tracks.map(t => t.id);
    const shuffled = await smartShuffle(ids);
    if (shuffled) {
      const trackMap = {};
      tracks.forEach(t => { trackMap[t.id] = t; });
      const newTracks = shuffled.map(id => trackMap[id]).filter(Boolean);
      setTracks(newTracks);
      // Save new order
      try {
        await axios.put(`${API}/playlists/${id}`, { track_ids: shuffled });
      } catch (e) {
        toast.error('Failed to save shuffled order');
      }
      const firstPlayable = newTracks.find(t => t.has_audio || t.storage_path);
      if (firstPlayable) {
        playTrack(firstPlayable, newTracks.filter(t => t.has_audio || t.storage_path));
        toast.success(`Shuffled ${newTracks.length} tracks — now playing`);
      } else {
        toast.info(`Shuffled ${newTracks.length} tracks (none have cloud audio to play)`);
      }
    }
  };


  const removeTrack = async (trackId) => {
    try {
      await axios.delete(`${API}/playlists/${id}/tracks/${trackId}`);
      toast.success('Track removed');
      fetchPlaylist();
    } catch (e) {
      toast.error('Failed to remove track');
    }
  };

  const updatePlaylistName = async () => {
    if (!editName.trim()) return;
    
    try {
      await axios.put(`${API}/playlists/${id}`, { name: editName });
      toast.success('Playlist updated');
      setEditing(false);
      fetchPlaylist();
    } catch (e) {
      toast.error('Failed to update playlist');
    }
  };

  const deletePlaylist = async () => {
    if (!window.confirm('Delete this playlist?')) return;
    
    try {
      await axios.delete(`${API}/playlists/${id}`);
      toast.success('Playlist deleted');
      refreshCounts();
      navigate('/playlists');
    } catch (e) {
      toast.error('Failed to delete playlist');
    }
  };

  const [showExportModal, setShowExportModal] = useState(false);
  const openExport = () => {
    if (tracks.length === 0) {
      toast.error('No tracks to export');
      return;
    }
    setShowExportModal(true);
  };

  const handlePlayTrack = (track) => {
    playTrack(track, tracks);
  };

  const handlePlayingChange = (playing) => {
    setIsPlaying(playing);
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getTotalDuration = () => {
    return tracks.reduce((sum, track) => sum + (track.duration || 0), 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#2563FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!playlist) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <ListMusic className="w-16 h-16 text-[#1E3A8A] mb-4" />
        <p className="font-heading text-xl text-[#64748B]">Playlist not found</p>
        <Link to="/playlists" className="text-[#2563FF] hover:text-[#3B82F6] mt-4">
          Back to Playlists
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <Link 
          to="/playlists" 
          className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#0F172A] mb-8 font-heading text-sm"
          data-testid="back-to-playlists"
        >
          <ArrowLeft className="w-4 h-4" />
          BACK TO PLAYLISTS
        </Link>

        {/* Playlist Header */}
        <div className="bg-white border border-[#E2E8F0] p-4 sm:p-8 mb-8">
          {/* Row 1 — icon + action buttons */}
          <div className="flex items-center justify-between mb-6">
            <div className="w-14 h-14 sm:w-20 sm:h-20 bg-[#EFF6FF] flex items-center justify-center flex-shrink-0">
              {playlist.is_suggested ? (
                <Sparkles className="w-7 h-7 sm:w-10 sm:h-10 text-[#2563FF]" />
              ) : (
                <ListMusic className="w-7 h-7 sm:w-10 sm:h-10 text-[#2563FF]" />
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {tracks.length > 0 && (
                <Button
                  onClick={() => handlePlayTrack(tracks[0])}
                  className="btn-primary"
                  data-testid="play-all-btn"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Play All
                </Button>
              )}
              {tracks.length >= 2 && (
                <Button
                  onClick={handleShuffle}
                  variant="outline"
                  className="border-[#E2E8F0] text-[#64748B] hover:text-[#2563FF] hover:border-[#2563FF]/50"
                  data-testid="shuffle-playlist-btn"
                  title="Smart Shuffle (Key + Energy)"
                >
                  <Shuffle className="w-4 h-4 mr-2" />
                  Shuffle
                </Button>
              )}
              <Button
                onClick={openExport}
                variant="outline"
                className="border-[#E2E8F0] text-[#64748B] hover:text-[#0F172A]"
                data-testid="export-playlist-btn"
                title="Export as M3U8"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
              <Button
                onClick={deletePlaylist}
                variant="outline"
                className="border-[#E2E8F0] text-[#64748B] hover:text-red-500"
                data-testid="delete-playlist-btn"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Row 3 — title + meta (2-line gap already provided by mb-6 above) */}
          {editing ? (
            <div className="flex items-center gap-3 mb-3">
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="bg-white border-[#E2E8F0] text-[#0F172A] text-xl sm:text-2xl font-heading"
                data-testid="edit-playlist-name"
              />
              <Button onClick={updatePlaylistName} className="btn-primary p-2">
                <Save className="w-5 h-5" />
              </Button>
              <Button onClick={() => setEditing(false)} variant="outline" className="p-2 border-[#E2E8F0]">
                <X className="w-5 h-5" />
              </Button>
            </div>
          ) : (
            <div className="flex items-start gap-2 mb-2">
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A] leading-tight" data-testid="playlist-name">
                {playlist.name}
              </h1>
              <button
                onClick={() => setEditing(true)}
                className="p-2 text-[#64748B] hover:text-[#2563FF] shrink-0 mt-1"
                data-testid="edit-name-btn"
              >
                <Edit2 className="w-4 h-4" />
              </button>
            </div>
          )}

          {playlist.is_suggested && (
            <span className="badge-data bg-[#EFF6FF] text-[#2563FF] border-[#E2E8F0] mb-3 inline-block">
              AI Generated
            </span>
          )}

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-xs sm:text-sm text-[#64748B] mb-2">
            <span>{tracks.length} tracks</span>
            <span>•</span>
            <span>{formatDuration(getTotalDuration())} total</span>
          </div>

          {playlist.description && (
            <p className="font-body text-sm text-[#64748B]">{playlist.description}</p>
          )}
        </div>

        {/* Phase 2 DJ Coach — Mix Quality Score + Transition Warnings */}
        {tracks.length >= 2 && (
          <div className="mb-8 space-y-3" data-testid="phase2-warnings-panel">
            <MixQualityCard
              warnings={setWarnings}
              trackCount={tracks.length}
              variant="full"
            />

            {/* Backend energy flow chart */}
            {setQuality?.energy_flow?.length >= 2 && (
              <div className="bg-white border border-[#E2E8F0] p-4 rounded-xl">
                <p className="font-mono text-xs text-[#64748B] uppercase tracking-wider mb-3 flex items-center gap-2">
                  <TrendingUp className="w-3.5 h-3.5" />
                  Energy Flow · {setQuality.track_count} tracks · Score: {setQuality.overall_score}/100
                </p>
                <div className="flex items-end gap-1 h-16">
                  {setQuality.energy_flow.map((point, i) => {
                    const h = Math.max(8, Math.round(point.energy * 100));
                    const transition = setQuality.transitions?.[i - 1];
                    const color = !transition ? '#2563FF'
                      : transition.verdict === 'clean' ? '#10B981'
                      : transition.verdict === 'caution' ? '#F59E0B'
                      : '#EF4444';
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
                        <div
                          className="w-full rounded-t transition-all duration-300"
                          style={{ height: `${h}%`, backgroundColor: color, opacity: 0.85 }}
                        />
                        <div className="absolute -top-14 left-1/2 -translate-x-1/2 bg-[#0F172A] text-white text-[9px] rounded px-1.5 py-1 hidden group-hover:block whitespace-nowrap z-10 pointer-events-none">
                          <p className="font-mono">{point.camelot} · {Math.round(point.bpm)} BPM</p>
                          <p className="text-[#94A3B8] truncate max-w-[120px]">{point.title}</p>
                        </div>
                        <span className="font-mono text-[8px] text-[#94A3B8]">{point.camelot}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center gap-4 mt-2 text-[10px] font-mono">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"/>Clean</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500 inline-block"/>Caution</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500 inline-block"/>Clash</span>
                </div>
              </div>
            )}

            {setWarnings.length > 0 && (
              <button
                type="button"
                onClick={() => setWarningsExpanded((v) => !v)}
                className="w-full px-4 py-2.5 rounded-xl border border-[#E2E8F0] bg-white/70 hover:bg-white text-left flex items-center justify-between transition-colors"
                data-testid="phase2-warnings-toggle"
              >
                <span className="text-xs font-mono text-[#475569] uppercase tracking-wider">
                  {warningsExpanded ? 'Hide' : 'Show'} all {setWarnings.length} transition warning{setWarnings.length === 1 ? '' : 's'}
                </span>
                {warningsExpanded
                  ? <ChevronUp className="w-4 h-4 text-[#64748B]" />
                  : <ChevronDown className="w-4 h-4 text-[#64748B]" />}
              </button>
            )}

            {setWarnings.length > 0 && warningsExpanded && (
              <div className="space-y-2 px-1" data-testid="phase2-warnings-list">
                {setWarnings.map((w, i) => {
                  const Icon = w.type === 'bpm_jump' ? TrendingUp : w.type === 'key_clash' ? KeyRound : Activity;
                  const tone = w.severity === 'high'
                    ? 'bg-red-100 text-red-800 border-red-200'
                    : w.severity === 'med'
                    ? 'bg-amber-100 text-amber-800 border-amber-200'
                    : 'bg-blue-100 text-blue-800 border-blue-200';
                  return (
                    <div
                      key={`${w.position}-${w.type}-${i}`}
                      className={`flex items-start gap-2.5 p-2.5 rounded-lg border ${tone}`}
                      data-testid={`phase2-warning-${i}`}
                    >
                      <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span className="font-mono text-xs leading-snug flex-1">{w.message}</span>
                      <span className="text-[10px] font-semibold uppercase tracking-wider opacity-80">
                        {w.severity}
                      </span>
                    </div>
                  );
                })}
                <p className="text-[11px] text-[#64748B] mt-3 italic px-2">
                  Tips: try reordering tracks with a drag-handle, or use Shuffle to let the AI Assistant re-pick the smoothest order.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Combined Playlist Energy Wave Visualization */}
        {tracks.length > 0 && (
          <div className="bg-white border border-[#E2E8F0] p-6 mb-8" data-testid="playlist-structure">
            <h3 className="font-heading text-lg text-[#0F172A] mb-4">PLAYLIST ENERGY</h3>
            
            {/* Combined energy wave */}
            <div className="relative">
              <div className="h-24 bg-white/30 border border-[#E2E8F0]" data-testid="combined-energy-wave">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={combinedEnergyData} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                    <defs>
                      <linearGradient id="playlistEnergyGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#2563FF" stopOpacity={0.9} />
                        <stop offset="50%" stopColor="#2563FF" stopOpacity={0.5} />
                        <stop offset="95%" stopColor="#2563FF" stopOpacity={0.1} />
                      </linearGradient>
                    </defs>
                    <Area 
                      type="monotone" 
                      dataKey="energy" 
                      stroke="#2563FF" 
                      strokeWidth={2} 
                      fill="url(#playlistEnergyGradient)" 
                      isAnimationActive={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              
              {/* Track divider markers */}
              {trackMarkers.map((marker, i) => (
                <div
                  key={i}
                  className="absolute top-0 h-full w-px bg-white/20"
                  style={{ left: `${marker.position}%` }}
                >
                  <span className="absolute -top-5 left-1/2 transform -translate-x-1/2 text-[10px] text-[#64748B] font-mono">
                    {marker.label}
                  </span>
                </div>
              ))}
            </div>
            
            {/* Track numbers below */}
            <div className="flex mt-2">
              {tracks.map((track, i) => {
                const totalDuration = tracks.reduce((sum, t) => sum + (t.duration || 180), 0);
                const widthPercent = ((track.duration || 180) / totalDuration) * 100;
                return (
                  <div 
                    key={track.id} 
                    className="text-center text-xs font-mono text-[#94A3B8] truncate px-1"
                    style={{ width: `${widthPercent}%` }}
                    title={track.title}
                  >
                    {i + 1}. {track.title.substring(0, 8)}{track.title.length > 8 ? '...' : ''}
                  </div>
                );
              })}
            </div>
            
            {/* Energy scale */}
            <div className="flex justify-between mt-3 text-xs font-mono text-[#94A3B8]">
              <span>Low Energy</span>
              <span>High Energy</span>
            </div>
          </div>
        )}

        {/* Drag instructions */}
        {tracks.length > 1 && (
          <p className="text-center text-[#94A3B8] font-mono text-xs mb-4">
            Drag tracks to reorder • Changes save automatically
          </p>
        )}

        {/* Column header — matches Crate layout */}
        {tracks.length > 0 && (
          <div
            className="grid px-4 py-2 text-xs font-mono text-[#94A3B8] uppercase tracking-widest border-b border-[#E2E8F0] mb-0"
            style={{ gridTemplateColumns: '40px 56px 24px 1fr 52px 76px 52px 160px 48px 40px', gap: '10px' }}
          >
            <div></div>
            <div></div>
            <div className="text-center">#</div>
            <div>Title / Artist</div>
            <div className="text-center">BPM</div>
            <div className="text-center">Key</div>
            <div className="text-center">Energy</div>
            <div className="text-center">Structure</div>
            <div className="text-right">Time</div>
            <div></div>
          </div>
        )}

        {/* Track List with Drag and Drop */}
        <div className="space-y-0 border border-[#E2E8F0] border-t-0">
          {tracks.length > 0 ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={tracks.map(t => t.id)} strategy={verticalListSortingStrategy}>
                {tracks.map((track, index) => (
                  <SortableTrackItem
                    key={track.id}
                    track={track}
                    index={index}
                    totalTracks={tracks.length}
                    nextTrack={tracks[index + 1]}
                    onRemove={removeTrack}
                    onPlay={handlePlayTrack}
                    isPlaying={isPlaying}
                    isCurrentTrack={playingTrack?.id === track.id}
                    formatDuration={formatDuration}
                    onTrackUpdate={(updated) => setTracks(prev => prev.map(t => t.id === updated.id ? { ...t, ...updated } : t))}
                  />
                ))}
              </SortableContext>
            </DndContext>
          ) : (
            <div className="text-center py-16 border border-[#E2E8F0] bg-white">
              <Music className="w-12 h-12 text-[#1E3A8A] mx-auto mb-3" />
              <p className="font-heading text-[#64748B]">No tracks in this playlist</p>
              <Link to="/library" className="text-[#2563FF] hover:text-[#3B82F6] text-[15px] mt-2 inline-block">
                Add tracks from library →
              </Link>
            </div>
          )}
        </div>

        {/* Spacer for audio player */}
        {playingTrack && <div className="h-32" />}
      </div>

      {showExportModal && (
        <ExportModal
          kind="playlist"
          id={id}
          name={playlist?.name}
          onClose={() => setShowExportModal(false)}
        />
      )}

      {/* Audio Player is now mounted globally in App.js via GlobalAudioPlayer */}
    </div>
  );
}
